import { useEffect, useState } from 'react'
import { Dialog } from './Dialog'
import { dateLocaleFor, useHubLang } from '../lib/i18n'
import { PHOTO_REVIEW } from '../lib/hubCopy'
import { messageOf } from '../lib/errors'
import {
  REVIEW_REASONS,
  consentWindows,
  myPhotoReviewState,
  requestPhotoReview,
  reviewNoteRequired,
  withdrawPhotoReview,
  type ConsentWindow,
  type MyReviewState,
  type ReviewReason,
} from '../lib/photoReview'

/**
 * 152. "Request review" under a photo — the feed viewer, the Gallery's browser
 * view and the document user's viewer all use THIS control (rule 11 for a
 * screen: one form, one set of words).
 *
 * Anybody who can see the photo may ask, except the person who posted it (their
 * own choices are the opt-out menu and Edit). It reads its own state with
 * my_photo_review_state unless the parent passes one, so a page of twenty
 * photos can ask once. A request in flight shows "You asked … Withdraw".
 *
 * The form says, before sending, who will see the name and that the poster will
 * not (the design's words), and — when "I don't consent" is ticked — that the
 * photo leaves every Gallery use NOW, because that is the one reason with an
 * immediate effect.
 *
 * 170 (David, 11 Oct): that reason has a WINDOW — 30 days from the post, or
 * from your tag if later — so nobody withdraws years of photos the week they
 * leave. The dialog reads the window when it opens: inside it, the date shows
 * under the reason; after it, the reason is not offered and a line says
 * "Something else" still reaches a reviewer. The server refuses a late consent
 * request regardless (request_photo_review).
 */
export function PhotoReviewButton({
  path,
  state: given,
  onChanged,
  className,
}: {
  path: string
  /** Pass when the parent already read it; otherwise it reads its own. */
  state?: MyReviewState | null
  /** After a request or a withdrawal: the parent may reload what it shows. */
  onChanged?: (path: string) => void
  className?: string
}) {
  const lang = useHubLang()
  const t = PHOTO_REVIEW[lang]
  // 170. undefined = not read yet; null = no answer (treated as open: the
  // server still decides).
  const [win, setWin] = useState<ConsentWindow | null | undefined>(undefined)
  const [state, setState] = useState<MyReviewState | null>(given ?? null)
  const [open, setOpen] = useState(false)
  const [reasons, setReasons] = useState<ReviewReason[]>([])
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [said, setSaid] = useState<string | null>(null)

  useEffect(() => {
    if (given !== undefined) {
      setState(given)
      return
    }
    let live = true
    setState(null)
    myPhotoReviewState([path])
      .then((m) => live && setState(m.get(path) ?? null))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [path, given])

  // Nothing to offer: the poster's own photo, a photo the server would not
  // admit the caller sees, or the state not read yet.
  if (!state || state.isMine) return said ? <p className="photoreview__said" role="status">{said}</p> : null

  const day = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(dateLocaleFor(lang), { day: 'numeric', month: 'long', year: 'numeric' }) : ''
  const consentClosed = !!win && !win.open
  const offered = REVIEW_REASONS.filter((r) => !(r === 'no_consent' && consentClosed))
  const needsNote = reviewNoteRequired(reasons)
  const canSend = reasons.length > 0 && (!needsNote || note.trim().length > 0) && !busy

  async function send() {
    setBusy(true)
    setError(null)
    try {
      const id = await requestPhotoReview(path, reasons, note)
      setState((s) => (s ? { ...s, canRequest: false, myReviewId: id, myReasons: reasons } : s))
      setOpen(false)
      setSaid(t.sent)
      onChanged?.(path)
    } catch (e) {
      setError(messageOf(e) || t.couldNotSend)
    } finally {
      setBusy(false)
    }
  }

  async function withdraw() {
    if (!state?.myReviewId) return
    setBusy(true)
    try {
      await withdrawPhotoReview(state.myReviewId)
      setState((s) => (s ? { ...s, canRequest: true, myReviewId: null, myReasons: [] } : s))
      setSaid(t.withdrawn)
      onChanged?.(path)
    } catch (e) {
      setSaid(messageOf(e) || t.couldNotSend)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={className ? `photoreview ${className}` : 'photoreview'}>
      {state.myReviewId ? (
        <p className="photoreview__line">
          <span>{t.requested}</span>{' '}
          <button type="button" className="btn btn--link" disabled={busy} onClick={() => void withdraw()}>
            {t.withdraw}
          </button>
        </p>
      ) : state.canRequest ? (
        <button
          type="button"
          className="btn btn--link photoreview__ask"
          onClick={() => {
            setReasons([])
            setNote('')
            setError(null)
            setSaid(null)
            setWin(undefined)
            setOpen(true)
            consentWindows([path])
              .then((m) => setWin(m.get(path) ?? null))
              .catch(() => setWin(null))
          }}
        >
          {t.requestButton}
        </button>
      ) : null}
      {said && (
        <p className="photoreview__said" role="status">
          {said}
        </p>
      )}

      {open && (
        <Dialog open title={t.dialogTitle} onClose={() => !busy && setOpen(false)} className="photoreview__dialog">
          <form
            className="photoreview__form"
            onSubmit={(e) => {
              e.preventDefault()
              if (canSend) void send()
            }}
          >
            <fieldset className="photoreview__reasons">
              <legend>{t.whyLegend}</legend>
              {offered.map((r) => (
                <label key={r} className="photoreview__reason">
                  <input
                    type="checkbox"
                    checked={reasons.includes(r)}
                    onChange={(e) =>
                      setReasons((cur) => (e.target.checked ? [...cur, r] : cur.filter((x) => x !== r)))
                    }
                  />
                  <span>
                    {t.reason[r]}
                    {r === 'no_consent' && win?.open && win.until && (
                      <span className="photoreview__hint photoreview__window">{t.consentUntil(day(win.until))}</span>
                    )}
                  </span>
                </label>
              ))}
              {consentClosed && <p className="photoreview__hint photoreview__closed">{t.consentClosed(day(win!.until))}</p>}
            </fieldset>
            <label className="photoreview__note">
              <span className="composer__label">{t.noteLabel}</span>
              <span className="photoreview__hint">{needsNote ? t.noteRequired : t.noteHint}</span>
              <textarea
                rows={3}
                maxLength={1000}
                value={note}
                required={needsNote}
                aria-invalid={needsNote && !note.trim() ? true : undefined}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
            {reasons.includes('no_consent') && <p className="photoreview__consent">{t.consentNow}</p>}
            <p className="photoreview__privacy">{t.privacy}</p>
            {error && (
              <p className="roles__error" role="alert">
                {error}
              </p>
            )}
            <div className="photoreview__actions">
              <button type="submit" className="btn btn--primary btn--sm" disabled={!canSend}>
                {busy ? t.sending : t.send}
              </button>
              <button type="button" className="btn btn--quiet btn--sm" disabled={busy} onClick={() => setOpen(false)}>
                {t.cancel}
              </button>
            </div>
          </form>
        </Dialog>
      )}
    </div>
  )
}
